import { customInstance } from '@/features/auth/axios-config'
import {
  RequestFilters,
  ResponseCompany,
  SuccessResponseListResponseCompany,
} from '@/schema'
import { mergeTablePaginationParams } from '@/utils'

import { Company, CreateCompanyRequest, UpdateCompanyRequest } from './types'

const API_ENDPOINT_COMPANIES = '/admin/v1/companies'

export interface Pagination {
  page: number
  has_next: false
  has_previous: false
  page_size: number
  total_items: number
  total_pages: number
}

export interface CompanyListResponse {
  data: Company[]
  success: boolean
  pagination: Pagination
}

export interface CompanyResponse {
  data: ResponseCompany[]
  success: boolean
}

export interface DeleteCompanyResponse {
  message: string
}

export const companyApi = {
  /**
   * Fetches all companies
   * @param search - Optional search query (minimum 3 characters)
   * @returns Promise with companies data and total count
   */
  getCompanyList: async (
    data: Partial<RequestFilters> = {}
  ): Promise<SuccessResponseListResponseCompany> => {
    const requestBody = mergeTablePaginationParams(data)

    return customInstance<SuccessResponseListResponseCompany>({
      method: 'post',
      url: `${API_ENDPOINT_COMPANIES}/list`,
      data: requestBody,
    })
  },

  getCompanyById: async (id: string): Promise<CompanyResponse> => {
    return customInstance<CompanyResponse>({
      method: 'get',
      url: `${API_ENDPOINT_COMPANIES}/${id}`,
    })
  },

  /**
   * Creates a new company
   * @param data - Company data
   * @returns Promise with created company
   */
  createCompany: async (
    data: CreateCompanyRequest
  ): Promise<CompanyResponse> => {
    return customInstance<CompanyResponse>({
      method: 'post',
      url: API_ENDPOINT_COMPANIES,
      data,
    })
  },

  /**
   * Updates an existing company
   * @param id - Company ID
   * @param data - Updated company data
   * @returns Promise with updated company
   */
  updateCompany: async (
    id: string,
    data: UpdateCompanyRequest
  ): Promise<CompanyResponse> => {
    return customInstance<CompanyResponse>({
      method: 'put',
      url: `${API_ENDPOINT_COMPANIES}/${id}`,
      data,
    })
  },

  /**
   * Deletes a company
   * @param id - Company ID
   * @returns Promise with deletion confirmation
   */
  deleteCompany: async (id: string): Promise<DeleteCompanyResponse> => {
    return customInstance<DeleteCompanyResponse>({
      method: 'delete',
      url: `${API_ENDPOINT_COMPANIES}/${id}`,
    })
  },
}
